import type {MouseEvent, PointerEvent, ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize} from '@Components/icon-v2/icon.types';
import {isStockFavoriteTogglePending, isStockItemFavorited} from '@Panels/stock-panel/stock-favorites-reducer';
import {toggleStockFavorite} from '@Panels/stock-panel/stock-favorites-thunk';
import {assertNever} from '@Utils/assert-never.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {FavoriteButtonProps, FavoriteButtonVariantClasses} from './favorite-button.types';
import {FavoriteButtonVariant, TILE_HEART_ADDED_ITEM_COUNT} from './favorite-button.types';
import {useFavoriteHeart} from './use-favorite-heart';
import styles from './favorite-button.module.scss';

/**
 * The heart that marks a stock item as a favorite. The tile underneath adds its item to the design on click and
 * starts a drag on pointerdown, so both events stop here. The favorited state comes from the favorites store,
 * so every tile showing the same item flips together.
 */
export function FavoriteButton({identity, variant}: FavoriteButtonProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const isFavorited = useAppSelector((state) => isStockItemFavorited(state, identity));
  const isTogglePending = useAppSelector((state) => isStockFavoriteTogglePending(state, identity));
  const heart = useFavoriteHeart({
    isFavorited,
    isTogglePending,
    addedItemCount: TILE_HEART_ADDED_ITEM_COUNT,
    requestToggle: () => dispatch(toggleStockFavorite(identity)),
  });

  if (heart === null) {
    return null;
  }

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    event.stopPropagation();
  };

  const onClick = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    heart.toggleHeart();
  };

  const getVariantClasses = (): FavoriteButtonVariantClasses => {
    switch (variant) {
      case FavoriteButtonVariant.MEDIA_OVERLAY:
        return {anchor: styles.overlayAnchor, button: styles.overlayButton};
      case FavoriteButtonVariant.INLINE:
        return {anchor: styles.inlineAnchor, button: styles.inlineButton};
      default:
        return assertNever(variant);
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <div className={variantClasses.anchor} onPointerDown={onPointerDown}>
      <Icon
        icon={heart.heartIcon}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_16}
        className={`${variantClasses.button} ${isFavorited ? styles.favorited : ''}`}
        isUsedAsButton
        ariaLabel={heart.accessibleLabel}
        onClick={onClick}
      />
    </div>
  );
}
